import { useCallback, useEffect, useState } from "react";
import type { PurchaseAttemptStatusResponse } from "../../shared/checkout";
import { formatTry } from "../../shared/pricing";

interface PurchaseStatusPageProps {
  attemptId: string;
  capabilityToken: string;
}

const terminalOutcomes = new Set(["published", "expired", "reconciliation", "refunded", "failed"]);

const copy = {
  pending: {
    eyebrow: "Ödeme doğrulanıyor",
    title: "Mesajın sıraya alındı.",
    body: "Stripe bildirimi güvenli biçimde doğrulanıyor. Bu sayfa mesajı yayınlamaz; yalnızca sunucudaki sonucu gösterir."
  },
  published: {
    eyebrow: "Yayınlandı",
    title: "Şehrin sesi değişti.",
    body: "Ödeme doğrulandı ve mesajın bugünün görünür şehir mesajı oldu."
  },
  expired: {
    eyebrow: "Süre doldu",
    title: "Rezervasyon kapandı.",
    body: "Ödeme tamamlanmadan beş dakikalık şehir rezervasyonu sona erdi. Fiyat değişmedi."
  },
  reconciliation: {
    eyebrow: "Kontrol gerekiyor",
    title: "Ödeme güvenli incelemede.",
    body: "Tutar alındıysa mesaj otomatik yayınlanmadı. Ekip ödeme ile şehir durumunu eşleştirip gerekirse tam iade başlatacak."
  },
  refunded: {
    eyebrow: "İade edildi",
    title: "Tutar geri gönderildi.",
    body: "İade Stripe üzerinden tamamlandı. Bankada görünmesi sağlayıcı işlem süresine bağlı olabilir."
  },
  failed: {
    eyebrow: "Tamamlanamadı",
    title: "Ödeme oturumu kurulamadı.",
    body: "Şehir rezervasyonu güvenli biçimde bırakıldı ve fiyat değişmedi."
  }
} as const;

export function PurchaseStatusPage({ attemptId, capabilityToken }: PurchaseStatusPageProps) {
  const [attempt, setAttempt] = useState<PurchaseAttemptStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(
        `/api/purchase-attempts/${encodeURIComponent(attemptId)}?token=${encodeURIComponent(capabilityToken)}`,
        { cache: "no-store" }
      );
      if (!response.ok) throw new Error("Durum bağlantısı doğrulanamadı.");
      setAttempt((await response.json()) as PurchaseAttemptStatusResponse);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Ödeme durumu alınamadı.");
    }
  }, [attemptId, capabilityToken]);

  useEffect(() => {
    const initial = window.setTimeout(() => void refresh(), 0);
    const interval = window.setInterval(() => void refresh(), 2_500);
    return () => {
      window.clearTimeout(initial);
      window.clearInterval(interval);
    };
  }, [refresh]);

  useEffect(() => {
    if (!attempt || !terminalOutcomes.has(attempt.outcome)) return;
    const title = copy[attempt.outcome].eyebrow;
    document.title = `${title} · Şehrin Sesi`;
  }, [attempt]);

  const outcome = attempt?.outcome ?? "pending";
  const content = copy[outcome];

  return (
    <main className="purchase-page">
      <a className="wordmark" href="/">
        Şehrin Sesi
      </a>
      <section className={`purchase-status purchase-status--${outcome}`} aria-live="polite">
        <div className="status-orbit" aria-hidden="true">
          <span />
        </div>
        <p className="eyebrow">{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className="purchase-status__body">{error ?? content.body}</p>
        {attempt && (
          <dl className="purchase-summary">
            <div>
              <dt>Şehir</dt>
              <dd>{attempt.cityCode}</dd>
            </div>
            <div>
              <dt>Tutar</dt>
              <dd>{formatTry(BigInt(attempt.amountMinor))}</dd>
            </div>
            <div>
              <dt>Durum</dt>
              <dd>{attempt.status}</dd>
            </div>
          </dl>
        )}
        {outcome === "pending" && (
          <button type="button" className="secondary-button" onClick={() => void refresh()}>
            Şimdi kontrol et
          </button>
        )}
        <a className="primary-link" href={attempt ? `/?city=${attempt.cityCode}` : "/"}>
          Haritaya dön
        </a>
      </section>
    </main>
  );
}
